import { create } from 'zustand';
import { api } from '../services/api';

export interface Document {
  id: string;
  family_id: string;
  title: string;
  description: string;
  file_url: string;
  file_type: string;
  file_size_bytes: number;
  category_id: string;
  tags: string[];
  expires_at?: string | null;
  is_sensitive?: boolean;
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentCategory {
  id: string;
  family_id: string;
  name: string;
  icon: string;
  colour: string;
  document_count: number;
  created_at: string;
  updated_at: string;
}

interface DocumentsState {
  documents: Document[];
  categories: DocumentCategory[];
  isLoading: boolean;
  error: string | null;
  fetchDocuments: () => Promise<void>;
  createDocument: (data: Partial<Document>) => Promise<Document>;
  updateDocument: (documentId: string, data: Partial<Document>) => Promise<void>;
  deleteDocument: (documentId: string) => Promise<void>;
  fetchCategories: () => Promise<void>;
  createCategory: (data: Partial<DocumentCategory>) => Promise<DocumentCategory>;
  updateCategory: (categoryId: string, data: Partial<DocumentCategory>) => Promise<void>;
  deleteCategory: (categoryId: string) => Promise<void>;
  handleWsMessage: (message: any) => void;
}

export const useDocumentsStore = create<DocumentsState>((set, get) => ({
  documents: [],
  categories: [],
  isLoading: false,
  error: null,

  fetchDocuments: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/documents');
      set({ documents: res.data.documents, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createDocument: async (data) => {
    try {
      // Documents may include a base64-encoded file in `file_url`, which can
      // be several megabytes. Extend the timeout well beyond the default 10s
      // and raise the max body size so axios does not reject the request.
      const res = await api.post('/documents', data, {
        timeout: 120000,
        maxContentLength: 20 * 1024 * 1024,
        maxBodyLength: 20 * 1024 * 1024,
      });
      const newDocument = res.data.document;
      set(state => ({ documents: [newDocument, ...state.documents] }));
      return newDocument;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateDocument: async (documentId, data) => {
    try {
      const res = await api.put(`/documents/${documentId}`, data);
      const updatedDocument = res.data.document;
      set(state => ({
        documents: state.documents.map(d => d.id === documentId ? updatedDocument : d),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteDocument: async (documentId) => {
    try {
      await api.delete(`/documents/${documentId}`);
      set(state => ({
        documents: state.documents.filter(d => d.id !== documentId),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  fetchCategories: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/documents/categories');
      set({ categories: res.data.categories, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createCategory: async (data) => {
    try {
      const res = await api.post('/documents/categories', data);
      const newCategory = res.data.category;
      set(state => ({ categories: [newCategory, ...state.categories] }));
      return newCategory;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateCategory: async (categoryId, data) => {
    try {
      const res = await api.put(`/documents/categories/${categoryId}`, data);
      const updatedCategory = res.data.category;
      set(state => ({
        categories: state.categories.map(c => c.id === categoryId ? updatedCategory : c),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteCategory: async (categoryId) => {
    try {
      await api.delete(`/documents/categories/${categoryId}`);
      set(state => ({
        categories: state.categories.filter(c => c.id !== categoryId),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  handleWsMessage: (message) => {
    switch (message.type) {
      case 'DOCUMENT_CREATED':
        set(state => {
          const exists = state.documents.find(d => d.id === message.payload.id);
          if (exists) return state;
          return { documents: [message.payload, ...state.documents] };
        });
        break;

      case 'DOCUMENT_UPDATED':
        set(state => ({
          documents: state.documents.map(d =>
            d.id === message.payload.id ? { ...d, ...message.payload } : d
          ),
        }));
        break;

      case 'DOCUMENT_DELETED':
        set(state => ({
          documents: state.documents.filter(d => d.id !== message.payload.id),
        }));
        break;

      case 'DOCUMENT_CATEGORY_CREATED':
        set(state => {
          const exists = state.categories.find(c => c.id === message.payload.id);
          if (exists) return state;
          return { categories: [message.payload, ...state.categories] };
        });
        break;

      case 'DOCUMENT_CATEGORY_UPDATED':
        set(state => ({
          categories: state.categories.map(c =>
            c.id === message.payload.id ? { ...c, ...message.payload } : c
          ),
        }));
        break;

      case 'DOCUMENT_CATEGORY_DELETED':
        set(state => ({
          categories: state.categories.filter(c => c.id !== message.payload.id),
        }));
        break;
    }
  },
}));
